import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  Dashboard,
  ViewCarousel,
  Build,
  Storefront,
  People,
  Category,
  Explore,
  Article,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  cmsService,
  CMSHeroSection,
  CMSService,
  CMSBrand,
  CMSProcessStep,
  CMSFeaturedEquipment,
  CMSTestimonial,
  CMSSiteSetting,
  CMSCustomer,
} from "../../services/cms";
import {
  headerCategoriesService,
  HeaderCategory,
} from "../../services/headerCategories";
import { subcategoriesService, Subcategory } from "../../services/subcategories";
import {
  exploreCategoriesService,
  CMSExploreCategory,
} from "../../services/exploreCategories";
import {
  mediBlogService,
  MediBlogArticle,
  MediBlogCategory,
} from "../../services/mediBlog";
import { resolveExploreCategoryRoute } from "../../utils/categoryRoutes";
import {
  checkAndCreateMediaBucket,
  CREATE_MEDIA_BUCKET_SQL,
} from "../../utils/checkStorage";

// Extracted Components
import DashboardOverview from "./cms/components/DashboardOverview";
import HeroSectionTab from "./cms/components/HeroSectionTab";
import ServicesTab from "./cms/components/ServicesTab";
import BrandsTab from "./cms/components/BrandsTab";
import HeaderCategoriesTab from "./cms/components/HeaderCategoriesTab";
import SubcategoriesTab from "./cms/components/SubcategoriesTab";
import ExploreCategoriesTab from "./cms/components/ExploreCategoriesTab";
import CustomersTab from "./cms/components/CustomersTab";
import HeroSectionDialog from "./cms/components/HeroSectionDialog";
import ServicesDialog from "./cms/components/ServicesDialog";
import BrandsDialog from "./cms/components/BrandsDialog";
import HeaderCategoriesDialog from "./cms/components/HeaderCategoriesDialog";
import SubcategoriesDialog from "./cms/components/SubcategoriesDialog";
import ExploreCategoriesDialog from "./cms/components/ExploreCategoriesDialog";
import CustomersDialog from "./cms/components/CustomersDialog";
import MediBlogTab from "./cms/components/MediBlogTab";
import MediBlogArticleDialog from "./cms/components/MediBlogArticleDialog";
import MediBlogCategoryDialog from "./cms/components/MediBlogCategoryDialog";
import CMSHeader from "./cms/components/CMSHeader";
import StorageCheckDialog from "./cms/components/StorageCheckDialog";

type EditableSubcategory = Omit<Subcategory, "id" | "created_at" | "updated_at"> & {
  id?: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cms-tabpanel-${index}`}
      aria-labelledby={`cms-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in timeout={300}>
          <Box sx={{ py: 3 }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

type EditableHeaderCategory = Omit<HeaderCategory, "id"> & { id?: string };

const ContentManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [storageCheckDialog, setStorageCheckDialog] = useState(false);
  const [storageCheckResult, setStorageCheckResult] = useState<{
    success?: boolean;
    error?: string;
    message?: string;
  } | null>(null);
  const [checkingStorage, setCheckingStorage] = useState(false);

  // State for different content types
  const [heroSections, setHeroSections] = useState<CMSHeroSection[]>([]);
  const [services, setServices] = useState<CMSService[]>([]);
  const [brands, setBrands] = useState<CMSBrand[]>([]);
  const [processSteps, setProcessSteps] = useState<CMSProcessStep[]>([]);
  const [featuredEquipment, setFeaturedEquipment] = useState<CMSFeaturedEquipment[]>([]);
  const [testimonials, setTestimonials] = useState<CMSTestimonial[]>([]);
  const [siteSettings, setSiteSettings] = useState<CMSSiteSetting[]>([]);
  const [headerCategories, setHeaderCategories] = useState<HeaderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [exploreCategories, setExploreCategories] = useState<CMSExploreCategory[]>([]);
  const [customers, setCustomers] = useState<CMSCustomer[]>([]);
  const [mediblogArticles, setMediblogArticles] = useState<MediBlogArticle[]>([]);
  const [mediblogCategories, setMediblogCategories] = useState<MediBlogCategory[]>([]);

  // Dialog states
  const [openHeroDialog, setOpenHeroDialog] = useState(false);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [openHeaderCategoryDialog, setOpenHeaderCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [openExploreCategoryDialog, setOpenExploreCategoryDialog] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openMediblogArticleDialog, setOpenMediblogArticleDialog] = useState(false);
  const [openMediblogCategoryDialog, setOpenMediblogCategoryDialog] = useState(false);

  // Form data states
  const [editingHero, setEditingHero] = useState<CMSHeroSection | null>(null);
  const [editingService, setEditingService] = useState<CMSService | null>(null);
  const [editingBrand, setEditingBrand] = useState<CMSBrand | null>(null);
  const [editingHeaderCategory, setEditingHeaderCategory] = useState<EditableHeaderCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<EditableSubcategory | null>(null);
  const [editingExploreCategory, setEditingExploreCategory] = useState<CMSExploreCategory | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<CMSCustomer | null>(null);
  const [editingMediblogArticle, setEditingMediblogArticle] = useState<MediBlogArticle | null>(null);
  const [editingMediblogCategory, setEditingMediblogCategory] = useState<MediBlogCategory | null>(null);

  useEffect(() => {
    fetchContentByTab(activeTab);
  }, [activeTab]);

   const fetchContentByTab = async (tabIndex: number) => {
    setLoading(true);
    try {
      switch (tabIndex) {
        case 0:
          await Promise.all([
            fetchHeroSections(),
            fetchServices(),
            fetchBrands(),
            fetchProcessSteps(),
            fetchFeaturedEquipment(),
            fetchTestimonials(),
            fetchSiteSettings(),
            fetchHeaderCategories(),
            fetchSubcategories(),
            fetchExploreCategories(),
            fetchCustomers(),
            fetchMediblogArticles(),
            fetchMediblogCategories(),
          ]);
          break;
        case 1: await fetchHeroSections(); break;
        case 2: await fetchServices(); break;
        case 3: await fetchBrands(); break;
        case 4: await fetchHeaderCategories(); break;
        case 5: await fetchSubcategories(); break;
        case 6: await fetchExploreCategories(); break;
        case 7: await fetchCustomers(); break;
        case 8:
          await Promise.all([fetchMediblogArticles(), fetchMediblogCategories()]);
          break;
      }
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshContent = async () => {
    setRefreshing(true);
    await fetchContentByTab(activeTab);
    setRefreshing(false);
  };

  const handleCheckStorage = async () => {
    setCheckingStorage(true);
    setStorageCheckResult(null);
    const result = await checkAndCreateMediaBucket();
    setStorageCheckResult(result);
    setCheckingStorage(false);
  };

  const handleCopySQL = () => {
    navigator.clipboard.writeText(CREATE_MEDIA_BUCKET_SQL);
    alert(t("admin.contentManagement.sqlCopied"));
  };

  const fetchHeroSections = async () => {
    const { data, error } = await cmsService.getHeroSections();
    if (!error && data) setHeroSections(data);
  };

  const fetchServices = async () => {
    const { data, error } = await cmsService.getServices();
    if (!error && data) setServices(data);
  };

  const fetchBrands = async () => {
    const { data, error } = await cmsService.getBrands();
    if (!error && data) setBrands(data);
  };

  const fetchProcessSteps = async () => {
    const { data, error } = await cmsService.getProcessSteps();
    if (!error && data) setProcessSteps(data);
  };

  const fetchFeaturedEquipment = async () => {
    const { data, error } = await cmsService.getFeaturedEquipment();
    if (!error && data) setFeaturedEquipment(data);
  };

  const fetchTestimonials = async () => {
    const { data, error } = await cmsService.getTestimonials();
    if (!error && data) setTestimonials(data);
  };

  const fetchSiteSettings = async () => {
    const { data, error } = await cmsService.getSiteSettings();
    if (!error && data) setSiteSettings(data);
  };

  const fetchHeaderCategories = async () => {
    const { data, error } = await headerCategoriesService.getCategories();
    if (!error && data) setHeaderCategories(data);
  };

  const fetchSubcategories = async () => {
    const { data, error } = await subcategoriesService.getAllSubcategories();
    if (error) {
      console.error("fetchSubcategories error:", error);
      return;
    }
    if (data) setSubcategories(data);
  };

  const fetchExploreCategories = async () => {
    const { data, error } = await exploreCategoriesService.getCategories();
    if (!error && data) setExploreCategories(data);
  };

  const fetchCustomers = async () => {
    const { data, error } = await cmsService.getCustomers();
    if (!error && data) setCustomers(data);
  };

  const fetchMediblogArticles = async () => {
    const { data } = await mediBlogService.getArticles();
    setMediblogArticles(data);
  };

  const fetchMediblogCategories = async () => {
    const { data } = await mediBlogService.getCategories();
    setMediblogCategories(data);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditHero = (hero?: CMSHeroSection) => {
    setEditingHero(hero || {
      title: "", subtitle: "", cta_text: "", cta_link: "", background_image: "",
      gradient_start: "#00d4ff", gradient_end: "#ff0080", is_active: true, display_order: 0,
    });
    setOpenHeroDialog(true);
  };

  const handleSaveHero = async () => {
    if (!editingHero) return;
    if (editingHero.id) await cmsService.updateHeroSection(editingHero.id, editingHero);
    else await cmsService.createHeroSection(editingHero);
    setOpenHeroDialog(false);
    await fetchHeroSections();
  };

  const handleDeleteHero = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.heroSection"))) {
      await cmsService.deleteHeroSection(id);
      await fetchHeroSections();
    }
  };

  const handleEditService = (service?: CMSService) => {
    setEditingService(service || {
      icon_name: "LocalShipping", title: "", description: "", gradient_start: "#00d4ff",
      gradient_end: "#0099cc", shadow_color: "rgba(0, 212, 255, 0.4)", is_active: true, display_order: 0,
    });
    setOpenServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!editingService) return;
    if (editingService.id) await cmsService.updateService(editingService.id, editingService);
    else await cmsService.createService(editingService);
    setOpenServiceDialog(false);
    await fetchServices();
  };

  const handleDeleteService = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.service"))) {
      await cmsService.deleteService(id);
      await fetchServices();
    }
  };

  const handleEditBrand = (brand?: CMSBrand) => {
    setEditingBrand(brand || {
      name: "", logo_url: "", website_url: "", description: "", is_active: true, display_order: 0,
    });
    setOpenBrandDialog(true);
  };

  const handleSaveBrand = async () => {
    if (!editingBrand) return;
    if (editingBrand.id) await cmsService.updateBrand(editingBrand.id, editingBrand);
    else await cmsService.createBrand(editingBrand);
    setOpenBrandDialog(false);
    await fetchBrands();
  };

  const handleDeleteBrand = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.brand"))) {
      await cmsService.deleteBrand(id);
      await fetchBrands();
    }
  };

  const handleEditHeaderCategory = (category?: HeaderCategory) => {
    if (category) {
      setEditingHeaderCategory({
        id: category.id, key: category.key, label_en: category.label_en || "",
        label_ar: category.label_ar || "", label_fr: category.label_fr || "",
        display_order: category.display_order, is_active: category.is_active,
      });
    } else {
      setEditingHeaderCategory({
        key: "", label_en: "", label_ar: "", label_fr: "", display_order: 0, is_active: true,
      });
    }
    setOpenHeaderCategoryDialog(true);
  };

  const handleSaveHeaderCategory = async () => {
    if (!editingHeaderCategory) return;
    const data = {
      key: editingHeaderCategory.key, label_en: editingHeaderCategory.label_en || undefined,
      label_ar: editingHeaderCategory.label_ar || undefined, label_fr: editingHeaderCategory.label_fr || undefined,
      display_order: editingHeaderCategory.display_order, is_active: editingHeaderCategory.is_active,
    };
    if (editingHeaderCategory.id) await headerCategoriesService.updateCategory(editingHeaderCategory.id, data);
    else await headerCategoriesService.createCategory(data);
    setOpenHeaderCategoryDialog(false);
    await fetchHeaderCategories();
  };

  const handleDeleteHeaderCategory = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.headerCategory"))) {
      await headerCategoriesService.deleteCategory(id);
      await fetchHeaderCategories();
    }
  };

  const handleEditSubcategory = (subcategory?: Subcategory) => {
    if (subcategory) {
      setEditingSubcategory({
        ...subcategory,
      });
    } else {
      setEditingSubcategory({
        category_key: "",
        key: "",
        label_en: "",
        label_ar: "",
        label_fr: "",
        parent_id: null,
        slug: "",
        meta_title: "",
        meta_description: "",
        display_order: 0,
        is_active: true,
      });
    }
    setOpenSubcategoryDialog(true);
  };

  const handleSaveSubcategory = async () => {
    if (!editingSubcategory) return;
    try {
      if (editingSubcategory.id) {
        const { category_key, key, label_en, label_ar, label_fr, parent_id, slug, meta_title, meta_description, display_order, is_active } = editingSubcategory;
        const { error } = await subcategoriesService.updateSubcategory(editingSubcategory.id, {
          category_key,
          key,
          label_en: label_en || undefined,
          label_ar: label_ar || undefined,
          label_fr: label_fr || undefined,
          parent_id: parent_id || null,
          slug: slug || undefined,
          meta_title: meta_title || undefined,
          meta_description: meta_description || undefined,
          display_order,
          is_active,
        });
        if (error) throw error;
      } else {
        const { error } = await subcategoriesService.createSubcategory(editingSubcategory);
        if (error) throw error;
      }
      setOpenSubcategoryDialog(false);
      await fetchSubcategories();
    } catch (err: any) {
      const isConflict = err?.code === '23505' || err?.status === 409 || String(err?.message).includes('duplicate');
      alert(isConflict
        ? t("admin.contentManagement.errors.duplicateKey", { defaultValue: "A subcategory with this key already exists. Please use a different key." })
        : t("common.error", { defaultValue: "Failed to save subcategory. Please try again." })
      );
    }
  };

  const handleDeleteSubcategory = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.subcategory"))) {
      await subcategoriesService.deleteSubcategory(id);
      await fetchSubcategories();
    }
  };

  const handleEditExploreCategory = (category?: CMSExploreCategory) => {
    setEditingExploreCategory(category || {
      title_key: "", title_en: "", title_ar: "", title_fr: "", image_url: "",
      card_key: "", target_category_key: "", target_subcategory_key: "",
      link_url: "", is_active: true, display_order: 0,
    });
    setOpenExploreCategoryDialog(true);
  };

  const handleSaveExploreCategory = async () => {
    if (!editingExploreCategory) return;
    if (!resolveExploreCategoryRoute(editingExploreCategory, subcategories)) return;
    if (editingExploreCategory.id) await exploreCategoriesService.updateCategory(editingExploreCategory.id, editingExploreCategory);
    else await exploreCategoriesService.createCategory(editingExploreCategory);
    setOpenExploreCategoryDialog(false);
    await fetchExploreCategories();
  };

  const handleDeleteExploreCategory = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.exploreCategory"))) {
      await exploreCategoriesService.deleteCategory(id);
      await fetchExploreCategories();
    }
  };

  const handleEditCustomer = (customer?: CMSCustomer) => {
    setEditingCustomer(customer || {
      name: "", logo_url: "", website_url: "", description: "", is_active: true, display_order: 0,
    });
    setOpenCustomerDialog(true);
  };

  const handleSaveCustomer = async () => {
    if (!editingCustomer) return;
    if (editingCustomer.id) await cmsService.updateCustomer(editingCustomer.id, editingCustomer);
    else await cmsService.createCustomer(editingCustomer);
    setOpenCustomerDialog(false);
    await fetchCustomers();
  };

  const handleDeleteCustomer = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.customer"))) {
      await cmsService.deleteCustomer(id);
      await fetchCustomers();
    }
  };

  const handleEditMediblogArticle = (article?: MediBlogArticle) => {
    setEditingMediblogArticle(
      article || {
        slug: "",
        title_en: "",
        content_en: "",
        status: "draft",
        display_order: 0,
        no_index: false,
      }
    );
    setOpenMediblogArticleDialog(true);
  };

  const handleSaveMediblogArticle = async () => {
    if (!editingMediblogArticle) return;
    if (editingMediblogArticle.id) {
      await mediBlogService.updateArticle(editingMediblogArticle.id, editingMediblogArticle);
    } else {
      await mediBlogService.createArticle(editingMediblogArticle);
    }
    setOpenMediblogArticleDialog(false);
    await fetchMediblogArticles();
  };

  const handleDeleteMediblogArticle = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.mediblogArticle"))) {
      await mediBlogService.deleteArticle(id);
      await fetchMediblogArticles();
    }
  };

  const handleEditMediblogCategory = (category?: MediBlogCategory) => {
    setEditingMediblogCategory(
      category || {
        name_en: "",
        slug: "",
        is_active: true,
        display_order: 0,
      }
    );
    setOpenMediblogCategoryDialog(true);
  };

  const handleSaveMediblogCategory = async () => {
    if (!editingMediblogCategory) return;
    if (editingMediblogCategory.id) {
      await mediBlogService.updateCategory(editingMediblogCategory.id, editingMediblogCategory);
    } else {
      await mediBlogService.createCategory(editingMediblogCategory);
    }
    setOpenMediblogCategoryDialog(false);
    await fetchMediblogCategories();
  };

  const handleDeleteMediblogCategory = async (id: string) => {
    if (window.confirm(t("admin.contentManagement.confirmDelete.mediblogCategory"))) {
      await mediBlogService.deleteCategory(id);
      await fetchMediblogCategories();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 4, sm: 5, md: 6 } }}>
      <CMSHeader 
        refreshing={refreshing} 
        onRefresh={refreshContent} 
        onOpenStorageDialog={() => setStorageCheckDialog(true)} 
      />

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid rgba(0,0,0,0.1)",
            "& .MuiTab-root": {
              fontWeight: 600, minHeight: { xs: 56, sm: 64 }, fontSize: { xs: "0.75rem", sm: "0.875rem" },
              "&.Mui-selected": { color: "#00d4ff" },
            },
            "& .MuiTabs-indicator": { bgcolor: "#00d4ff", height: 3 },
          }}
        >
          <Tab icon={<Dashboard />} label={t("admin.contentManagement.tabs.overview")} />
          <Tab icon={<ViewCarousel />} label={t("admin.contentManagement.tabs.heroSections")} />
          <Tab icon={<Build />} label={t("admin.contentManagement.tabs.services")} />
          <Tab icon={<Storefront />} label={t("admin.contentManagement.tabs.brands")} />
          <Tab icon={<Category />} label={t("admin.contentManagement.tabs.headerCategories")} />
          <Tab icon={<Category />} label={t("admin.contentManagement.tabs.subcategories")} />
          <Tab icon={<Explore />} label={t("admin.contentManagement.tabs.exploreCategories")} />
          <Tab icon={<People />} label={t("admin.contentManagement.tabs.customers")} />
          <Tab icon={<Article />} label={t("admin.contentManagement.tabs.mediblog")} />
        </Tabs>

        <Box sx={{ p: 3, minHeight: "60vh" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
          ) : (
            <>
              <TabPanel value={activeTab} index={0}>
                <DashboardOverview
                  stats={{
                    heroSections: heroSections.length, services: services.length,
                    brands: brands.length, processSteps: processSteps.length,
                    featuredEquipment: featuredEquipment.length, testimonials: testimonials.length,
                    siteSettings: siteSettings.length, headerCategories: headerCategories.length,
                    exploreCategories: exploreCategories.length, customers: customers.length,
                  }}
                  onTabChange={setActiveTab}
                />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <HeroSectionTab heroSections={heroSections} onEdit={handleEditHero} onDelete={handleDeleteHero} />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <ServicesTab services={services} onEdit={handleEditService} onDelete={handleDeleteService} />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <BrandsTab brands={brands} onEdit={handleEditBrand} onDelete={handleDeleteBrand} />
              </TabPanel>
               <TabPanel value={activeTab} index={4}>
                 <HeaderCategoriesTab categories={headerCategories} onEdit={handleEditHeaderCategory} onDelete={handleDeleteHeaderCategory} />
               </TabPanel>
               <TabPanel value={activeTab} index={5}>
                 <SubcategoriesTab subcategories={subcategories} onEdit={handleEditSubcategory} onDelete={handleDeleteSubcategory} />
               </TabPanel>
               <TabPanel value={activeTab} index={6}>
                 <ExploreCategoriesTab categories={exploreCategories} subcategories={subcategories} onEdit={handleEditExploreCategory} onDelete={handleDeleteExploreCategory} />
               </TabPanel>
               <TabPanel value={activeTab} index={7}>
                 <CustomersTab customers={customers} onEdit={handleEditCustomer} onDelete={handleDeleteCustomer} />
               </TabPanel>
               <TabPanel value={activeTab} index={8}>
                 <MediBlogTab
                   articles={mediblogArticles}
                   categories={mediblogCategories}
                   onEditArticle={handleEditMediblogArticle}
                   onDeleteArticle={handleDeleteMediblogArticle}
                   onEditCategory={handleEditMediblogCategory}
                   onDeleteCategory={handleDeleteMediblogCategory}
                 />
               </TabPanel>
            </>
          )}
        </Box>
      </Paper>

       <HeroSectionDialog open={openHeroDialog} onClose={() => setOpenHeroDialog(false)} onSave={handleSaveHero} editingHero={editingHero} setEditingHero={setEditingHero} />
       <ServicesDialog open={openServiceDialog} onClose={() => setOpenServiceDialog(false)} onSave={handleSaveService} editingService={editingService} setEditingService={setEditingService} />
       <BrandsDialog open={openBrandDialog} onClose={() => setOpenBrandDialog(false)} onSave={handleSaveBrand} editingBrand={editingBrand} setEditingBrand={setEditingBrand} />
       <HeaderCategoriesDialog open={openHeaderCategoryDialog} onClose={() => setOpenHeaderCategoryDialog(false)} onSave={handleSaveHeaderCategory} editingHeaderCategory={editingHeaderCategory} setEditingHeaderCategory={setEditingHeaderCategory} />
       <SubcategoriesDialog open={openSubcategoryDialog} onClose={() => setOpenSubcategoryDialog(false)} onSave={handleSaveSubcategory} editingSubcategory={editingSubcategory} setEditingSubcategory={setEditingSubcategory} allSubcategories={subcategories} />
       <ExploreCategoriesDialog open={openExploreCategoryDialog} onClose={() => setOpenExploreCategoryDialog(false)} onSave={handleSaveExploreCategory} editingExploreCategory={editingExploreCategory} setEditingExploreCategory={setEditingExploreCategory} headerCategories={headerCategories} subcategories={subcategories} />
       <CustomersDialog open={openCustomerDialog} onClose={() => setOpenCustomerDialog(false)} onSave={handleSaveCustomer} editingCustomer={editingCustomer} setEditingCustomer={setEditingCustomer} />
       <MediBlogArticleDialog
         open={openMediblogArticleDialog}
         onClose={() => setOpenMediblogArticleDialog(false)}
         onSave={handleSaveMediblogArticle}
         editingArticle={editingMediblogArticle}
         setEditingArticle={setEditingMediblogArticle}
         categories={mediblogCategories}
       />
       <MediBlogCategoryDialog
         open={openMediblogCategoryDialog}
         onClose={() => setOpenMediblogCategoryDialog(false)}
         onSave={handleSaveMediblogCategory}
         editingCategory={editingMediblogCategory}
         setEditingCategory={setEditingMediblogCategory}
       />
      
      <StorageCheckDialog 
        open={storageCheckDialog} 
        onClose={() => setStorageCheckDialog(false)} 
        checking={checkingStorage} 
        result={storageCheckResult} 
        onCheck={handleCheckStorage} 
        onCopySQL={handleCopySQL} 
      />
    </Container>
  );
};

export default ContentManagement;
